/**
 * Shared session status display utilities.
 * Used by scheduler, payments, and other features.
 */

/** Shown when no session is booked yet (UI + consolidated export). */
export const TO_BE_SCHEDULED_DISPLAY = 'To Be Scheduled';

/** Normalize for comparing slug/display variants (e.g. to_be_scheduled, To Be Scheduled). */
const normalizedSessionStatusWords = (status: string): string =>
  String(status)
    .trim()
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export const isSessionStatusEmpty = (
  status: string | null | undefined,
): boolean => {
  if (status === null || status === undefined) return true;
  const t = String(status).trim();
  if (t === '') return true;
  if (t === '-' || t === '—' || t === '–') return true;
  if (normalizedSessionStatusWords(t) === 'to be scheduled') return true;
  return false;
};

export const SESSION_STATUS_DISPLAY: Record<string, string> = {
  scheduled: 'Scheduled',
  'in progress': 'In Progress',
  in_progress: 'In Progress',
  inprogress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
  canceled: 'Cancelled',
  'called off': 'Called Off',
  called_off: 'Called Off',
  calledoff: 'Called Off',
  'no show': 'No Show',
  'no-show': 'No Show',
  no_show: 'No Show',
  noshow: 'No Show',
  reschedule: 'Pending Reschedule',
  rescheduled: 'Rescheduled',
  'not started': 'Scheduled',
  not_started: 'Scheduled',
  notstarted: 'Scheduled',
  active: 'Scheduled',
  booked: 'Scheduled',
  'partially completed': 'Partially Completed',
  partially_completed: 'Partially Completed',
  disqualified: 'Disqualified',
  blocked: 'Do Not Hire',
  overbooked: 'Overbooked',
  max_overbooks_reached: 'Overbooked',
};

export const formatSessionStatusDisplay = (status: string | null | undefined): string => {
  if (isSessionStatusEmpty(status)) {
    return TO_BE_SCHEDULED_DISPLAY;
  }
  const raw = String(status);
  const key = raw.toLowerCase().trim().replace(/\s+/g, ' ');
  const keyWithUnderscores = key.replace(/\s+/g, '_');
  return (
    SESSION_STATUS_DISPLAY[key] ??
    SESSION_STATUS_DISPLAY[keyWithUnderscores] ??
    raw.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  );
};

/**
 * Session **cell** label: Do not hire (`blocked`) shows as "-" per product spec;
 * use {@link formatSessionStatusDisplay} for filters and other places that need the full label.
 */
export const formatSessionStatusCellDisplay = (
  status: string | null | undefined,
): string => {
  if (isSessionStatusEmpty(status)) {
    return formatSessionStatusDisplay(status);
  }
  const raw = String(status);
  const key = raw.toLowerCase().trim().replace(/\s+/g, ' ');
  const keyWithUnderscores = key.replace(/\s+/g, '_');
  if (key === 'blocked' || keyWithUnderscores === 'blocked') {
    return '-';
  }
  return formatSessionStatusDisplay(status);
};

export type SessionStatusBadgeVariant = 'sessionStatus';

/** Single pill style for every session status (matches Completed / “To Be Scheduled” UI). */
export const getSessionStatusBadge = (
  status: string | null | undefined,
): { variant: SessionStatusBadgeVariant; text: string } => ({
  variant: 'sessionStatus',
  text: formatSessionStatusCellDisplay(status),
});
